import { stripToDigits } from '@/lib/phone'
import { SALON_DESK_CHECKOUT } from '@/lib/salonDeskConfig'

/** Minimum characters before POS customer search runs. */
export const POS_CUSTOMER_SEARCH_MIN_LENGTH = 2

/** Debounce delay for phone/name search (ms). */
export const POS_CUSTOMER_SEARCH_DEBOUNCE_MS = 300

/**
 * @param {string} query
 */
export function shouldSearchCustomers(query) {
  const trimmed = query.trim()
  if (trimmed.length < POS_CUSTOMER_SEARCH_MIN_LENGTH) return false
  return true
}

/**
 * @param {import('@/types/api').Customer[]} customers
 * @param {string} phoneE164
 */
export function findExactCustomerByPhone(customers, phoneE164) {
  if (!phoneE164) return null
  const target = stripToDigits(phoneE164)
  return customers.find((customer) => stripToDigits(customer.phone) === target) ?? null
}

/**
 * Whether POS should offer walk-in quick register for the current search.
 * @param {object} params
 * @param {import('@/types/api').Customer[]} params.customers
 * @param {string} params.query
 * @param {string} params.phoneE164
 * @param {boolean} [params.registerMode]
 */
export function shouldOfferQuickRegister({
  customers,
  query,
  phoneE164,
  registerMode = false,
}) {
  if (registerMode) return true
  if (!shouldSearchCustomers(query)) return false
  if (phoneE164) {
    return findExactCustomerByPhone(customers, phoneE164) == null
  }
  return customers.length === 0
}

/**
 * @param {import('@/types/api').Sale | null | undefined} sale
 */
export function getAssignedCustomer(sale) {
  return sale?.customer ?? null
}

/**
 * @param {import('@/types/api').Sale | null | undefined} sale
 */
export function hasAssignedCustomer(sale) {
  const customer = getAssignedCustomer(sale)
  return Boolean(customer?.id && String(customer.name ?? '').trim() && String(customer.phone ?? '').trim())
}

/**
 * @param {import('@/types/api').Sale | null | undefined} sale
 */
export function saleMeetsGuestRequirement(sale) {
  if (!SALON_DESK_CHECKOUT.requireGuestForCheckout) {
    return true
  }
  return hasAssignedCustomer(sale)
}

/**
 * @param {import('@/types/api').Sale | null | undefined} sale
 */
export function getSaleGenderPrice(sale) {
  const gender = sale?.gender_price
  if (gender === 'ladies' || gender === 'gents') return gender
  return null
}

/**
 * @param {import('@/types/api').Customer | { id: number, name: string, phone: string, gender?: string | null }} customer
 */
export function isCustomerBlacklisted(customer) {
  return Boolean(customer?.blacklist)
}
